import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { UserTramsAccount } from "@shared/schema";

export interface TramsAccountContext {
  tramsAccountId: number;
  profileId: number;
  agentName: string;
  interfaceId: string;
}

export function useTramsAccounts() {
  return useQuery<UserTramsAccount[]>({
    queryKey: ["/api/trams-accounts/mine"],
    queryFn: () => apiRequest("GET", "/api/trams-accounts/mine"),
    staleTime: 60_000,
  });
}

export function useCurrentTramsContext() {
  return useQuery<{ context: TramsAccountContext | null }>({
    queryKey: ["/api/trams-accounts/current"],
    queryFn: () => apiRequest("GET", "/api/trams-accounts/current"),
    staleTime: 60_000,
  });
}

export function useSelectTramsAccount() {
  const queryClient = useQueryClient();

  return useMutation<
    { context: TramsAccountContext },
    Error,
    { tramsAccountId: number }
  >({
    mutationFn: (data) =>
      apiRequest("POST", "/api/trams-accounts/select", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/trams-accounts/current"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices/mine"] });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.some(
            (key) =>
              typeof key === "string" &&
              (key.includes("/invoices") || key.includes("/api/trips/")),
          ),
      });
    },
  });
}

export function useTramsAccountsForUser(userId: number | undefined) {
  return useQuery<UserTramsAccount[]>({
    queryKey: ["/api/trams-accounts/user", userId],
    queryFn: () => apiRequest("GET", `/api/trams-accounts/user/${userId}`),
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function useCreateTramsAccount() {
  const queryClient = useQueryClient();

  return useMutation<
    UserTramsAccount,
    Error,
    { userId: number; profileId: number; agentName: string; interfaceId: string; corporateId?: number }
  >({
    mutationFn: (data) => apiRequest("POST", "/api/trams-accounts", data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/trams-accounts/user", variables.userId] });
      queryClient.invalidateQueries({ queryKey: ["/api/trams-accounts/mine"] });
      queryClient.invalidateQueries({ queryKey: ["/api/trams-accounts/current"] });
    },
  });
}

export function useUpdateTramsAccount() {
  const queryClient = useQueryClient();

  return useMutation<
    UserTramsAccount,
    Error,
    { id: number; data: { agentName?: string } }
  >({
    mutationFn: ({ id, data }) => apiRequest("PATCH", `/api/trams-accounts/${id}`, data),
    onSuccess: (updatedAccount) => {
      queryClient.invalidateQueries({ queryKey: ["/api/trams-accounts/user", updatedAccount.userId] });
      queryClient.invalidateQueries({ queryKey: ["/api/trams-accounts/mine"] });
      queryClient.invalidateQueries({ queryKey: ["/api/trams-accounts/current"] });
    },
  });
}
